import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import ExpandableSection from '../../expandableSection';

const ReasonsRemarksSection = ({
  expanded,
  onToggle,
  onDownload,
  remarks,
  primaryReasonOptions,
  secondaryReasonOptions,
  onPrimaryReasonChange,
  onSecondaryReasonChange,
  onRemarksChange,
  onSave,
  isSaving,
  isLoading,
  isDisabled,
}) => {
  const primaryReasonPlaceholder = isLoading ? 'Loading primary reasons...' : 'Select primary reason';
  const secondaryReasonPlaceholder = isLoading ? 'Loading secondary reasons...' : 'Select secondary reason';
  const [primaryInputValue, setPrimaryInputValue] = useState('');
  const [secondaryInputValue, setSecondaryInputValue] = useState('');
  const [isPrimaryOpen, setIsPrimaryOpen] = useState(false);
  const [isSecondaryOpen, setIsSecondaryOpen] = useState(false);

  useEffect(() => {
    setPrimaryInputValue('');
    setIsPrimaryOpen(false);
  }, [primaryReasonOptions]);

  useEffect(() => {
    setSecondaryInputValue('');
    setIsSecondaryOpen(false);
  }, [secondaryReasonOptions]);

  return (
    <ExpandableSection
      title="Reasons and Remarks for Effort Deviation"
      expanded={expanded}
      onToggle={onToggle}
      onDownload={onDownload}
      showDownloadAction={false}
      showMoreAction={false}
    >
      <div className="section-content reasons-remarks-section">
        <div className="reasons-remarks-shell">
          <div className="reasons-remarks-form">
            <div className="reasons-remarks-grid">
              <label className="reasons-field" htmlFor="effort-primary-reason">
                <span className="reasons-field-label">Primary reason</span>
                <Autocomplete
                  id="effort-primary-reason"
                  className="reasons-autocomplete"
                  value={null}
                  inputValue={primaryInputValue}
                  open={isPrimaryOpen}
                  options={primaryReasonOptions}
                  autoHighlight={false}
                  autoSelect={false}
                  clearOnBlur={false}
                  selectOnFocus={false}
                  handleHomeEndKeys
                  openOnFocus={false}
                  getOptionLabel={(option) => option?.primaryReasons || ''}
                  isOptionEqualToValue={(option, value) => (
                    String(option?.primaryReasonId) === String(value?.primaryReasonId)
                  )}
                  onInputChange={(event, newInputValue, reason) => {
                    void event;
                    if (reason === 'reset') return;
                    setPrimaryInputValue(newInputValue);
                    setIsPrimaryOpen(reason === 'input' && String(newInputValue || '').trim().length > 0);
                    if (!String(newInputValue || '').trim()) {
                      onPrimaryReasonChange('');
                    }
                  }}
                  onChange={(event, newValue) => {
                    void event;
                    setPrimaryInputValue(newValue?.primaryReasons || '');
                    setIsPrimaryOpen(false);
                    onPrimaryReasonChange(String(newValue?.primaryReasonId || ''));
                  }}
                  onOpen={() => {}}
                  onClose={() => setIsPrimaryOpen(false)}
                  disabled={isDisabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={primaryReasonPlaceholder}
                      className="input-field"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  slotProps={{
                    paper: {
                      className: 'dropdown-paper reasons-dropdown-paper',
                    },
                  }}
                />
              </label>

              <label className="reasons-field" htmlFor="effort-secondary-reason">
                <span className="reasons-field-label">Secondary reason</span>
                <Autocomplete
                  id="effort-secondary-reason"
                  className="reasons-autocomplete"
                  value={null}
                  inputValue={secondaryInputValue}
                  open={isSecondaryOpen}
                  options={secondaryReasonOptions}
                  autoHighlight={false}
                  autoSelect={false}
                  clearOnBlur={false}
                  selectOnFocus={false}
                  handleHomeEndKeys
                  openOnFocus={false}
                  getOptionLabel={(option) => option?.secReasons || ''}
                  isOptionEqualToValue={(option, value) => (
                    String(option?.secReasonId) === String(value?.secReasonId)
                  )}
                  onInputChange={(event, newInputValue, reason) => {
                    void event;
                    if (reason === 'reset') return;
                    setSecondaryInputValue(newInputValue);
                    setIsSecondaryOpen(reason === 'input' && String(newInputValue || '').trim().length > 0);
                    if (!String(newInputValue || '').trim()) {
                      onSecondaryReasonChange('');
                    }
                  }}
                  onChange={(event, newValue) => {
                    void event;
                    setSecondaryInputValue(newValue?.secReasons || '');
                    setIsSecondaryOpen(false);
                    onSecondaryReasonChange(String(newValue?.secReasonId || ''));
                  }}
                  onOpen={() => {}}
                  onClose={() => setIsSecondaryOpen(false)}
                  disabled={isDisabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={secondaryReasonPlaceholder}
                      className="input-field"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  slotProps={{
                    paper: {
                      className: 'dropdown-paper reasons-dropdown-paper',
                    },
                  }}
                />
              </label>
            </div>

            <label className="reasons-field reasons-field--full" htmlFor="effort-deviation-remarks">
              <span className="reasons-field-label">Remarks for effort deviation</span>
              <textarea
                id="effort-deviation-remarks"
                rows="4"
                value={remarks}
                onChange={(event) => onRemarksChange(event.target.value)}
                placeholder="Add remarks for effort deviation"
                disabled={isDisabled}
              />
            </label>

            <div className="reasons-remarks-footer">
              <button
                type="button"
                className="reasons-save-btn"
                disabled={isDisabled || isSaving}
                onClick={onSave}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ExpandableSection>
  );
};

ReasonsRemarksSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  remarks: PropTypes.string,
  primaryReasonOptions: PropTypes.arrayOf(PropTypes.shape({
    primaryReasonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    primaryReasons: PropTypes.string,
  })),
  secondaryReasonOptions: PropTypes.arrayOf(PropTypes.shape({
    secReasonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secReasons: PropTypes.string,
  })),
  onPrimaryReasonChange: PropTypes.func,
  onSecondaryReasonChange: PropTypes.func,
  onRemarksChange: PropTypes.func,
  onSave: PropTypes.func,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

ReasonsRemarksSection.defaultProps = {
  onDownload: null,
  remarks: '',
  primaryReasonOptions: [],
  secondaryReasonOptions: [],
  onPrimaryReasonChange: () => {},
  onSecondaryReasonChange: () => {},
  onRemarksChange: () => {},
  onSave: () => {},
  isSaving: false,
  isLoading: false,
  isDisabled: false,
};

export default ReasonsRemarksSection;
